import React, { useEffect, useRef, useState } from "react";
import "./FissureGrouting.css";

// Gallery image imports — replace with your actual image files
import gallery1 from "../assets/gallery/Fgaller1.jpeg";
import gallery2 from "../assets/gallery/Fgaller2.jpeg";
import gallery3 from "../assets/gallery/Fgaller3.jpeg";
import gallery4 from "../assets/gallery/Fgaller4.jpeg";
import gallery5 from "../assets/gallery/Fgaller5.jpeg";
import gallery6 from "../assets/gallery/Fgaller6.jpeg";
import gallery7 from "../assets/gallery/Fgaller7.jpeg";
import gallery8 from "../assets/gallery/Fgaller8.jpeg";
import overviewImg from "../assets/gallery/fg-gallery-1.jpeg";

const GALLERY_IMAGES = [
  { src: gallery1},
  { src: gallery2},
  { src: gallery3},
  { src: gallery4},
  { src: gallery5},
  { src: gallery6},
  { src: gallery7},
  { src: gallery8},
];

/* ── Main FissureGrouting Component ── */
const FissureGrouting = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = {};
    Object.keys(sectionRefs.current).forEach((key) => {
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.15 }
      );
      if (sectionRefs.current[key]) {
        observers[key].observe(sectionRefs.current[key]);
      }
    });
    return () => {
      Object.values(observers).forEach((obs) => obs.disconnect());
    };
  }, []);

  const setRef = (key) => (el) => {
    sectionRefs.current[key] = el;
  };

  const methodsData = [
    {
      number: "01",
      title: "Borehole Drilling & Preparation",
      desc: "Rotary percussive drilling to target depth, with hole flushing and inspection prior to grout injection.",
      icon: "⬇",
    },
    {
      number: "02",
      title: "Water Pressure Testing (Lugeon)",
      desc: "Pre-grouting permeability assessment to identify fissure zones and determine appropriate grouting pressures.",
      icon: "💧",
    },
    {
      number: "03",
      title: "Packer Injection",
      desc: "Single or double packer systems isolate target zones for controlled, stage-by-stage grout injection into fissures.",
      icon: "🔧",
    },
    {
      number: "04",
      title: "Cement & Chemical Grouting",
      desc: "Low-mobility cement grout or chemical grout injected under pressure to seal fissures and reduce rock mass permeability.",
      icon: "💉",
    },
    {
      number: "05",
      title: "Grout Mix Design & QC",
      desc: "Laboratory-verified grout mix proportioning with on-site batching control and fluidity, bleed, and set-time testing.",
      icon: "🔬",
    },
    {
      number: "06",
      title: "Verification & Reporting",
      desc: "Post-grouting permeability re-testing with pressure and volume logging, supported by detailed grouting records.",
      icon: "📋",
    },
  ];

  return (
    <div className="fg-page" id="#fissure-grouting">
      {/* Top Banner */}
      <section className="fg-top-banner">
        <div className="fg-top-banner__grid" />
        <div className="fg-top-banner__glow" />
        <h1 className="fg-top-banner__title">
          Fissure <span className="fg-text-accent">Grouting</span>
        </h1>
      </section>

      {/* Overview Section */}
      <section
        className={`fg-overview ${isVisible["overview"] ? "fg-animate-in" : ""}`}
        ref={setRef("overview")}
      >
        <div className="fg-overview__inner">
          <div className="fg-overview__left">
            <div className="fg-section-label">
              <span className="fg-section-label__line" />
              WHAT WE DO
            </div>
            <h2 className="fg-section-title">
              SEALING THE
              <br />
              <span className="fg-text-accent">VOIDS BELOW</span>
            </h2>
            <p className="fg-overview__text">
              Fractured rock and permeable ground can compromise excavation stability and
              groundwater control. GSK Geotech's Fissure Grouting service injects cement
              or chemical grout under controlled pressure to fill fissures, joints, and
              voids — strengthening rock mass and reducing seepage.
            </p>
            <p className="fg-overview__text">
              Our experienced grouting team has executed fissure grouting works across
              major tunnelling, shaft, and excavation projects, delivering measurable
              permeability reduction backed by Lugeon testing and full injection records.
            </p>
            <div className="fg-overview__highlights">
              {[
                "Experienced fissure grouting specialists",
                "Lugeon water pressure testing & verification",
                "End-to-end: drilling, injection, reporting",
                "Compliant with industry & regulatory standards",
              ].map((item, i) => (
                <div key={i} className="fg-overview__highlight-item">
                  <span className="fg-overview__highlight-icon">✦</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="fg-overview__right">
            <div className="fg-overview__image-block">
              <img
                src={gallery1}
                alt="Fissure Grouting Rock Mass Improvement Works"
                className="fg-overview__image"
              />
              <div className="fg-overview__stat-card">
                <span className="fg-overview__stat-number">4+</span>
                <span className="fg-overview__stat-text">GROUTING<br />PROJECTS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods Section */}
      <section
        className={`fg-methods ${isVisible["methods"] ? "fg-animate-in" : ""}`}
        ref={setRef("methods")}
      >
        <div className="fg-methods__inner">
          <div className="fg-section-label">
            <span className="fg-section-label__line" />
            OUR APPROACH
          </div>
          <h2 className="fg-section-title">
            GROUTING
            <br />
            <span className="fg-text-accent">METHODS</span>
          </h2>
          <p className="fg-methods__subtitle">
            From borehole drilling to verified sealing — we deploy a full suite of
            fissure grouting techniques tailored to each site's rock conditions.
          </p>
          <div className="fg-methods__grid">
            {methodsData.map((method, i) => (
              <div
                key={i}
                className="fg-method-card"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="fg-method-card__number">{method.number}</div>
                <div className="fg-method-card__icon">{method.icon}</div>
                <h3 className="fg-method-card__title">{method.title}</h3>
                <p className="fg-method-card__desc">{method.desc}</p>
                <div className="fg-method-card__line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        className={`fg-process ${isVisible["process"] ? "fg-animate-in" : ""}`}
        ref={setRef("process")}
      >
        <div className="fg-process__inner">
          <div className="fg-section-label">
            <span className="fg-section-label__line" />
            HOW WE WORK
          </div>
          <h2 className="fg-section-title">
            OUR <span className="fg-text-accent">PROCESS</span>
          </h2>
          <div className="fg-process__steps">
            {[
              { step: "01", title: "Site Mobilisation", desc: "Grout plant setup, drilling rig positioning, safety checks and permit acquisition prior to works." },
              { step: "02", title: "Drilling & Pressure Testing", desc: "Boreholes drilled to target depth with Lugeon water pressure testing to map fissure permeability." },
              { step: "03", title: "Packer Injection", desc: "Packers isolate target zones for controlled, stage-by-stage grout injection at rising or descending intervals." },
              { step: "04", title: "Pressure & Volume Monitoring", desc: "Real-time logging of injection pressure, volume, and flow rate against pre-set refusal criteria." },
              { step: "05", title: "Verification & Reporting", desc: "Post-grouting permeability re-testing with detailed injection records and interpretive performance report." },
            ].map((item, i) => (
              <div key={i} className="fg-process__step">
                <div className="fg-process__step-num">{item.step}</div>
                <div className="fg-process__step-connector" />
                <div className="fg-process__step-content">
                  <h4 className="fg-process__step-title">{item.title}</h4>
                  <p className="fg-process__step-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section
        className={`fg-projects ${isVisible["projects"] ? "fg-animate-in" : ""}`}
        ref={setRef("projects")}
      >
        <div className="fg-projects__inner">
          <div className="fg-section-label">
            <span className="fg-section-label__line" />
            TRACK RECORD
          </div>
          <h2 className="fg-section-title">
            COMPLETED FISSURE
            <br />
            <span className="fg-text-accent">GROUTING PROJECTS</span>
          </h2>
          <div className="fg-projects__grid">
            {[
              { title: "Drilling and Technical Support for Fissure Grouting for DTSS Phase 2 Link Sewer LK11C4", client: "Jae Hwan Construction Pte Ltd" },
              { title: "Fissure and TAM Grouting for DTSS Phase 2 Schedule 1 Contract 4", client: "MK Tunnel Pte Ltd" },
              { title: "TAM/FISSURE grouting Work at DTSS Phase Link Sewers 2 Schedule 2 Contract 3B", client: "Megastones Holdings Pte Ltd" },
              { title: "FISSURE grouting works at DTSS Phase 2 LK2C1", client: "King View Engineering Pte Ltd" },
            ].map((proj, i) => (
              <div key={i} className="fg-project-card">
                <h4 className="fg-project-card__title">{proj.title}</h4>
                <p className="fg-project-card__client">{proj.client}</p>
              </div>
            ))}
          </div>
          <div className="fg-projects__cta">
            <a href="/projects" className="fg-btn fg-btn--outline">
              VIEW ALL PROJECTS →
            </a>
          </div>
        </div>
      </section>

      {/* Masonry Gallery */}
      <section className="fg-gallery">
        <div className="fg-gallery__inner">
          <div className="fg-section-label">
            <span className="fg-section-label__line" />
            SITE PHOTOS
          </div>
          <h2 className="fg-section-title">
            PROJECT <span className="fg-text-accent">GALLERY</span>
          </h2>
          <div className="fg-gallery__masonry">
            {GALLERY_IMAGES.map((img, i) => (
              <div key={i} className="fg-gallery__item">
                <img src={img.src} alt={img.caption} className="fg-gallery__img" />
                <div className="fg-gallery__overlay">
                  <span className="fg-gallery__caption">{img.caption}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="fg-cta">
        <div className="fg-cta__inner">
          <div className="fg-cta__left">
            <p className="fg-cta__label">READY TO START?</p>
            <h2 className="fg-cta__title">
              NEED FISSURE GROUTING
              <br />
              <span className="fg-text-accent">FOR YOUR PROJECT?</span>
            </h2>
            <p className="fg-cta__desc">
              Our team is ready to mobilise. Get in touch for a detailed quote
              and consultation tailored to your project's ground sealing requirements.
            </p>
          </div>
          <div className="fg-cta__right">
            <a href="/contact-us" className="fg-btn fg-btn--primary fg-btn--large">
              CONTACT US →
            </a>
            <div className="fg-cta__contacts">
              <span>Nedu: +65 9239 8494</span>
              <span>Suben: +65 9027 1160</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FissureGrouting;